import type { ObservationRow } from "../types";

/** The most cards side by side in the Latest observations panel. */
export const MAX_CARDS_PER_ROW = 3;

/** Narrowest a card may get before the panel shows fewer per row. Text is never cut short: a line too long for its
 *  card shrinks its fonts to fit (``FitLine``), so this only has to leave room for them to stay legible. */
export const MIN_CARD_WIDTH_PX = 190;

/** How many cards fit side by side in a panel this wide (at least 1, at most ``MAX_CARDS_PER_ROW``). */
export function cardsThatFit(panelWidth: number, gap: number): number {
  const fit = Math.floor((panelWidth + gap) / (MIN_CARD_WIDTH_PX + gap));
  return Math.min(MAX_CARDS_PER_ROW, Math.max(1, fit));
}

/**
 * Card counts per row for ``count`` cards, ``perRow`` at most: as few rows as that allows, then the cards
 * shared out evenly between them, longer rows first. 4 cards in rows of up to 3 are 2 + 2, not 3 + 1, so no
 * card is left alone stretched across the whole panel.
 */
export function rowSizes(count: number, perRow: number): number[] {
  if (count <= 0) return [];
  const rows = Math.ceil(count / Math.max(1, perRow));
  const base = Math.floor(count / rows);
  const longer = count % rows;
  return Array.from({ length: rows }, (_, i) => base + (i < longer ? 1 : 0));
}

/** ``items`` cut into consecutive rows of the given sizes. */
export function intoRows<T>(items: T[], sizes: number[]): T[][] {
  const rows: T[][] = [];
  let start = 0;
  for (const size of sizes) {
    rows.push(items.slice(start, start + size));
    start += size;
  }
  return rows;
}

/**
 * What a card says beside its reading: "↑ from 120 mmHg", "→ from 144", "first reading". It states
 * arithmetic the reducer computed, never a characterisation of it. There is deliberately no colour coding by
 * value: that would be the interface making a clinical claim the product does not make.
 */
export function changeNote(
  row: Pick<ObservationRow, "previous_value" | "direction" | "unit">,
  withUnit: boolean,
): string {
  if (row.previous_value === null || row.previous_value === undefined) return "first reading";
  const arrow = row.direction === "unchanged" ? "→" : row.direction === "higher" ? "↑" : "↓";
  return `${arrow} from ${row.previous_value}${withUnit ? ` ${row.unit}` : ""}`;
}
